// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject, TemplateRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { TEMPLATE_IDENTIFIERS } from '@lfx/key-projects/key-projects.model';

/**
 * Meeting attendance data model from SQL query (API returns uppercase keys)
 */
export interface MeetingAttendanceData {
  USER_FULL_NAME: string;
  USER_PHOTO_URL?: string;
  MEETING_TYPE: string;
  MEETING_DATE: string;
  MEETING_NAME: string;
  ACCOUNT_NAME: string;
  ORGANIZATION_LOGO?: string;
  PROJECT_NAME: string;
  MEETING_HOURS: number;
  IS_ALL_PROJECTS: boolean;
}

/**
 * Meeting attendance summary data model from SQL query
 */
export interface MeetingAttendanceSummaryData {
  totalMeetings: number;
  totalMeetingHours: number;
  averageHoursPerMeeting: number;
}

/**
 * Entity service for meeting attendance metric type
 * Handles SQL query: platinum_organization_dashboard_meeting_attendance_detail
 */
@Injectable({
  providedIn: 'root'
})
export class MeetingAttendanceMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  private readonly config = {
    definition: 'Individual attendance records for foundation meetings, including committee meetings, board meetings, and project-specific gatherings.',
    columns: [
      { name: 'Our meeting attendees', prop: 'displayName', templateId: TEMPLATE_IDENTIFIERS.USER_PROFILE },
      { name: 'Meeting type', prop: 'meetingType' },
      { name: 'Meeting date', prop: 'meetingDate' }
    ],
    summaryColumns: [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total count for this project', prop: 'totalMeetings' },
      { name: 'Data source', prop: 'dataSource' }
    ],
    dataSource: 'LFX'
  };

  /**
   * Get columns for meeting attendance table
   * Based on UI requirements for meeting attendance display
   */
  getColumns(): Column[] {
    return this.config.columns.map(col => {
      const { templateId, ...columnProps } = col;
      return {
        ...columnProps,
        ...(templateId && { template: templateId as unknown as TemplateRef<unknown> })
      };
    });
  }

  /**
   * Get API endpoint for meeting attendance
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/meeting-attendance`;
  }

  /**
   * Fetch meeting attendance data from backend
   * Executes SQL: SELECT user_full_name, user_photo_url, committee_type AS meeting_type,
   * meeting_date, meeting_name, account_name, organization_logo, project_name, meeting_hours, is_all_projects
   * FROM platinum_organization_dashboard_meeting_attendance_detail
   * WHERE account_id = :account_id AND (:project_slug IS NULL OR project_slug = :project_slug) AND is_all_projects = TRUE
   * ORDER BY meeting_date DESC, user_full_name ASC, meeting_hours DESC
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<MeetingAttendanceData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch meeting attendance data');
            }

            return response.data.map(attendance => this.transformToDataRow(attendance));
          }),
          catchError(error => {
            console.error('Error fetching meeting attendance data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in meeting attendance entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform meeting attendance data to DataRow format for the table
   */
  private transformToDataRow(attendance: MeetingAttendanceData): DataRow {
    return {
      displayName: {
        text: attendance.USER_FULL_NAME,
        extraData: {
          imageUrl: attendance.USER_PHOTO_URL,
          userId: attendance.USER_FULL_NAME, // Using name as ID since no separate user ID provided
          memberDisplayName: attendance.USER_FULL_NAME,
          memberLogoUrl: attendance.USER_PHOTO_URL
        }
      },
      meetingType: { text: attendance.MEETING_TYPE },
      meetingDate: { text: this.formatDate(attendance.MEETING_DATE) }
    };
  }

  /**
   * Fetch meeting attendance summary data from backend
   * Executes SQL: SELECT COUNT(*) AS total_meeting_attendance, SUM(meeting_hours) AS total_meeting_hours,
   * AVG(meeting_hours) AS average_hours_per_meeting
   * FROM platinum_organization_dashboard_meeting_attendance_detail
   * WHERE account_id = :account_id AND (:project_slug IS NULL OR project_slug = :project_slug) AND is_all_projects = TRUE
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: MeetingAttendanceSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch meeting attendance summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = this.config.summaryColumns;

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalMeetings: { text: response.data.totalMeetings.toLocaleString('en-US') },
                dataSource: { text: this.config.dataSource }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching meeting attendance summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = this.config.summaryColumns;
            const fallbackData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalMeetings: { text: 'N/A' },
                dataSource: { text: this.config.dataSource }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in meeting attendance summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = this.config.summaryColumns;
        const fallbackData: DataRow[] = [
          {
            definition: { text: this.config.definition },
            totalMeetings: { text: 'N/A' },
            dataSource: { text: this.config.dataSource }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}
